import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

const SOCKET_SERVER_URL = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? (process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001')
    : '';
/**
 * usePulseEngine (V1.2 - Industrial WebSocket Edition)
 *
 * Manages the ONE Socket.IO connection for the entire app (instantiated once in App.js's
 * AppContent). Provides: global online-user count, per-poll "watchers" counts (via
 * join/leave "poll room" events driven by IntersectionObserver visibility in PollCard.js),
 * and authoritative vote-tally push updates that override the optimistic local guess from
 * useOptimisticVote.js.
 *
 * userId identification: the socket-setup effect below (connection init + all event
 * listeners) still runs exactly ONCE on mount and is intentionally NOT re-run when `userId`
 * changes — tearing down/recreating the WebSocket connection itself on every login would drop
 * rooms and force reconnects, which is disruptive. Instead:
 *   - `userIdRef` always holds the latest `userId` (kept in sync by a tiny effect below), so
 *     the 'connect' handler's reconnect-resync loop and `registerVisibility` always emit the
 *     CURRENT id even though they're defined inside callbacks with an empty dependency array.
 *   - A separate, small effect re-emits 'identify' whenever `userId` changes (e.g. a guest
 *     verifies/logs in mid-session), so the backend picks up the new identity immediately
 *     instead of requiring a full page reload.
 */
export function usePulseEngine(userId) {
    const [onlineTotal, setOnlineTotal] = useState(1);
    const [pollExposure, setPollExposure] = useState({});
    const [status, setStatus] = useState('connecting');
    const [pollUpdates, setPollUpdates] = useState({});
    const socketRef = useRef(null);
    const visiblePollsRef = useRef(new Set());
    const userIdRef = useRef(userId);

    const [lastSync, setLastSync] = useState(null);
    const [serverTime, setServerTime] = useState(Date.now());

    // Keep the ref current so callbacks defined with empty dependency arrays (the 'connect'
    // handler's resync loop, registerVisibility) can still read the latest userId.
    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    // Server Time Sync
    useEffect(() => {
        const syncTime = async () => {
            try {
                const response = await fetch(`${SOCKET_SERVER_URL}/health`);
                const data = await response.json();
                if (data.time) setServerTime(new Date(data.time).getTime());
                else setServerTime(Date.now());
            } catch (e) {
                setServerTime(Date.now());
            }
        };
        syncTime();
        const interval = setInterval(syncTime, 30000); // Sync every 30s
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // 1. Initialize Socket.io Connection.
        // Empty dependency array below: this connection is established exactly ONCE for the
        // component's lifetime (App.js never unmounts AppContent), and is never torn down and
        // recreated when `userId` changes later (e.g. after login). See the docstring above —
        // a separate effect handles re-emitting 'identify' when userId changes instead.
        const socket = io(SOCKET_SERVER_URL, {
            transports: ['websocket', 'polling'], // polling as fallback if WS upgrade fails
            reconnectionAttempts: 20,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 10000,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('[Pulse] ⚡ Industrial Gateway Connected');
            setStatus('online');
            setLastSync(new Date().toLocaleTimeString());

            // Deduplication: Identify this user to the server. Reads from the ref (not the
            // `userId` closed over at effect-setup time) so a reconnect after login still
            // identifies with the current id.
            if (userIdRef.current) {
                socket.emit('identify', { userId: userIdRef.current });
            }

            // RESYNC ON RECONNECT:
            // If the socket dies and recovers, re-join all rooms for polls currently on-screen
            visiblePollsRef.current.forEach(pollId => {
                socket.emit('join_poll_room', { pollId, userId: userIdRef.current });
                console.log(`[Pulse] Resyncing Room Join: ${pollId}`);
            });
        });

        // 2. Global Ticker (All Citizens Online)
        socket.on('global_update', (data) => {
            if (data.onlineTotal !== undefined) {
                setOnlineTotal(data.onlineTotal);
                setLastSync(new Date().toLocaleTimeString());
            }
        });

        // 3. Room-Specific Watchers (People looking at specific polls)
        socket.on('room_update', (data) => {
            if (data.pollId && data.watchers !== undefined) {
                setPollExposure(prev => ({
                    ...prev,
                    [data.pollId]: data.watchers
                }));
                setLastSync(new Date().toLocaleTimeString());
            }
        });

        // 4. Odometer Sync (Official Weighted Truth)
        socket.on('poll_update', (data) => {
            if (data.pollId) {
                setPollUpdates(prev => ({
                    ...prev,
                    [data.pollId]: data
                }));
                setLastSync(new Date().toLocaleTimeString());
            }
        });

        socket.on('disconnect', () => {
            console.warn('[Pulse] ⚠️ Industrial Gateway Offline');
            setStatus('offline');
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    // Re-identify on userId change (e.g. guest verifies/logs in mid-session) WITHOUT tearing
    // down and recreating the socket connection itself — only emits 'identify' on the
    // existing, already-open connection. Guarded on `socket.connected` so this doesn't fire
    // (or loop) while the connection is still being established; the 'connect' handler above
    // covers that initial case (and any reconnect) via `userIdRef`.
    useEffect(() => {
        const socket = socketRef.current;
        if (socket && socket.connected && userId) {
            socket.emit('identify', { userId });
            console.log(`[Pulse] Re-identified as ${userId}`);
        }
    }, [userId]);

    // Function to register a poll as visible (triggering room join/leave).
    // Deps are empty ([]) so this callback's identity stays stable across renders, but it
    // reads `userId` via `userIdRef.current` (not the `userId` param directly) so join/leave
    // events always carry the CURRENT id, even after a login changes it post-mount.
    const registerVisibility = useCallback((pollId, isVisible) => {
        const socket = socketRef.current;
        if (!socket || !socket.connected) return;

        if (isVisible && !visiblePollsRef.current.has(pollId)) {
            visiblePollsRef.current.add(pollId);
            socket.emit('join_poll_room', { pollId, userId: userIdRef.current });
            console.log(`[Pulse] Join Room: ${pollId}`);
        } else if (!isVisible && visiblePollsRef.current.has(pollId)) {
            visiblePollsRef.current.delete(pollId);
            socket.emit('leave_poll_room', { pollId, userId: userIdRef.current });
            console.log(`[Pulse] Leave Room: ${pollId}`);
        }
    }, []);

    // PulseId for debugging: use the socket ID
    const pulseId = socketRef.current?.id || 'CONNECTING...';

    return {
        onlineTotal,
        pollExposure,
        pollUpdates,
        registerVisibility,
        pulseId,
        status,
        lastSync,
        serverTime
    };
}
